import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatCard from "@/components/StatCard";
import OnboardingChecklist from "@/components/OnboardingChecklist";
import StudioNudge from "@/components/StudioNudge";
import UsdcToken from "@/components/landing-v2/UsdcToken";
import { Sparkles, ArrowUpRight, RefreshCw, FileText, X } from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";
import { toast } from "sonner";

const COLORS = { Invoices: "#E8B96C" };

const Dashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [forecastLoading, setForecastLoading] = useState(false);
  const chartRef = useRef(null);
  const [chartSize, setChartSize] = useState({ w: 0, h: 288 });
  const [taxPackDismissed, setTaxPackDismissed] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.localStorage.getItem("quill_tax_pack_banner_dismissed") === "1";
  });

  const dismissTaxPack = () => {
    setTaxPackDismissed(true);
    try {
      window.localStorage.setItem("quill_tax_pack_banner_dismissed", "1");
    } catch {
      /* localStorage unavailable */
    }
  };

  useEffect(() => {
    if (!chartRef.current) return;
    const el = chartRef.current;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) {
          setChartSize({ w: Math.floor(width), h: Math.floor(height) });
        }
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [data]);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/dashboard");
      setData(r.data);
    } catch (e) {
      console.error("Failed to load dashboard:", e);
      toast.error("Could not load dashboard");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const runForecast = async () => {
    setForecastLoading(true);
    try {
      const r = await api.post("/ai/forecast");
      setForecast(r.data.forecast);
    } catch (e) {
      toast.error("AI forecast failed");
    } finally {
      setForecastLoading(false);
    }
  };

  if (!data) {
    return <div className="p-10 text-zinc-500 text-sm">Loading dashboard…</div>;
  }
  const k = data.kpis;

  return (
    <div data-testid="dashboard-page">
      {!taxPackDismissed && (
        <div
          className="relative border border-[#2c241e] bg-[#1f1815] mx-4 md:mx-0 mt-4 md:mt-0 px-4 md:px-5 py-4 md:mb-6 flex items-center gap-3 md:gap-4"
          data-testid="tax-pack-banner"
        >
          <div className="hidden sm:flex h-9 w-9 items-center justify-center border border-[#2c241e] bg-[#1a1410] shrink-0">
            <FileText size={14} className="text-amber-300/80" strokeWidth={2} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-amber-300/80">
              Tax Season Pack · 2025
            </div>
            <div className="text-sm text-zinc-200 mt-1 truncate">
              Filing-ready IRS 8949 + CSV for every crypto invoice. Refundable $29 founder price.
            </div>
          </div>
          <a
            href="/tax-pack"
            data-testid="tax-pack-banner-cta"
            className="hidden sm:inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white hover:text-amber-300 transition-colors"
          >
            Pre-order <ArrowUpRight size={12} />
          </a>
          <button
            type="button"
            onClick={dismissTaxPack}
            aria-label="Dismiss"
            data-testid="tax-pack-banner-dismiss"
            className="ml-2 p-2 -my-1 text-zinc-500 hover:text-white rounded-md hover:bg-white/5"
          >
            <X size={14} />
          </button>
        </div>
      )}
      <PageHeader
        overline="Overview"
        title="Dashboard"
        description="A live view of your earnings, pending invoices, and next payout."
        ornament={
          <div style={{ width: 180, transform: "scale(0.85)" }}>
            <UsdcToken />
          </div>
        }
        actions={
          <button
            onClick={runForecast}
            data-testid="ai-forecast-btn"
            disabled={forecastLoading}
            className="inline-flex items-center gap-2 bg-white text-black text-sm font-medium px-4 py-2 rounded-md hover:bg-zinc-200 disabled:opacity-50"
          >
            {forecastLoading ? <RefreshCw size={14} className="animate-spin" /> : <Sparkles size={14} strokeWidth={2.5} />}
            AI forecast
          </button>
        }
        testId="dashboard-header"
      />

      <OnboardingChecklist onboarding={data.onboarding} onRefresh={load} />

      <StudioNudge variant="dashboard" />

      {/* KPIs — pastel paper cards bridging landing diorama into workspace */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 px-4 md:px-8 pt-4 md:pt-6">
        <StatCard
          label="Total earnings"
          value={fmtMoney(k.total_earnings)}
          sub="From invoices"
          tone="cream"
          testId="kpi-total-earnings"
        />
        <StatCard
          label="Pending"
          value={fmtMoney(k.pending_amount)}
          sub={`${k.pending_count} invoice${k.pending_count === 1 ? "" : "s"} open`}
          tone="peach"
          testId="kpi-pending"
        />
        <StatCard
          label="Paid"
          value={String(k.paid_count ?? 0)}
          sub={`Invoice${(k.paid_count ?? 0) === 1 ? "" : "s"} settled`}
          tone="pink"
          testId="kpi-paid"
        />
        <StatCard
          label="Next payout"
          value={fmtMoney(k.next_payout)}
          sub="Wallet balance"
          tone="mint"
          testId="kpi-next-payout"
        />
      </div>

      {/* Forecast */}
      {forecast && (
        <div className="quill-paper mx-4 md:mx-8 mt-4 md:mt-6 p-5 md:p-7" data-testid="forecast-card">
          <div className="quill-paper-rule" />
          <div className="relative pl-5 md:pl-8">
            <div className="text-[10px] uppercase tracking-[0.24em] flex items-center gap-2" style={{ color: "hsl(var(--quill-warm))" }}>
              <Sparkles size={12} /> AI Forecast · Claude Sonnet 4.5
            </div>
            <p className="text-zinc-200 mt-3 whitespace-pre-wrap text-sm leading-relaxed max-w-3xl">{forecast}</p>
          </div>
        </div>
      )}

      {/* Chart */}
      <div className="quill-paper mx-4 md:mx-8 mt-4 md:mt-6 p-5 md:p-8">
        <div className="quill-paper-rule" />
        <div className="relative pl-5 md:pl-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em]" style={{ color: "hsl(var(--quill-warm))" }}>Invoice earnings</div>
              <div className="font-display text-xl md:text-2xl tracking-tight mt-1 text-zinc-100">Last 6 months</div>
            </div>
          </div>
          <div ref={chartRef} className="h-72 w-full min-w-0">
            {chartSize.w > 0 && (
              <AreaChart
                width={chartSize.w}
                height={chartSize.h}
                data={data.chart}
                margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
              >
                <defs>
                  {Object.entries(COLORS).map(([k, c]) => (
                    <linearGradient key={k} id={`g-${k}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={c} stopOpacity={0.4} />
                      <stop offset="95%" stopColor={c} stopOpacity={0} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid stroke="#3b2c22" vertical={false} strokeDasharray="2 4" />
                <XAxis dataKey="month" stroke="#8a7565" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} axisLine={{ stroke: "#3b2c22" }} tickLine={{ stroke: "#3b2c22" }} />
                <YAxis stroke="#8a7565" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} axisLine={{ stroke: "#3b2c22" }} tickLine={{ stroke: "#3b2c22" }} />
                <Tooltip
                  contentStyle={{ background: "#251b15", border: "1px solid #3b2c22", borderRadius: 4, fontSize: 12 }}
                  labelStyle={{ color: "#e8d5c0" }}
                />
                <Legend wrapperStyle={{ fontSize: 11, color: "#c9b8a8" }} />
                {Object.entries(COLORS).map(([key, c]) => (
                  <Area key={key} type="monotone" dataKey={key} stackId="1" stroke={c} fill={`url(#g-${key})`} strokeWidth={1.5} />
                ))}
              </AreaChart>
            )}
          </div>
        </div>
      </div>

      {/* Recent invoices */}
      <div className="quill-paper quill-paper-ledger mx-4 md:mx-8 my-4 md:my-6 p-5 md:p-8">
        <div className="quill-paper-rule" />
        <div className="relative pl-5 md:pl-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em]" style={{ color: "hsl(var(--quill-warm))" }}>Recent invoices</div>
              <div className="font-display text-lg md:text-xl tracking-tight mt-1 text-zinc-100">Latest activity</div>
            </div>
            <button
              onClick={() => navigate("/invoices")}
              data-testid="view-all-invoices-btn"
              className="text-sm text-zinc-400 hover:text-white inline-flex items-center gap-1 transition-colors"
            >
              View all <ArrowUpRight size={14} />
            </button>
          </div>
          <div className="border border-[#35271e] rounded-sm overflow-x-auto bg-[#1b130f]/40">
            {data.recent_invoices.length === 0 ? (
              <div className="p-8 text-center text-zinc-500 text-sm" data-testid="no-invoices-msg">
                No invoices yet. Create your first one to see activity here.
              </div>
            ) : (
              <table className="w-full md:min-w-[480px]">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500 border-b border-[#35271e]">
                    <th className="p-3 md:p-4 font-normal">Number</th>
                    <th className="p-3 md:p-4 font-normal">Client</th>
                    <th className="p-3 md:p-4 font-normal text-right">Total</th>
                    <th className="p-3 md:p-4 font-normal">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_invoices.map((inv) => (
                    <tr key={inv.invoice_id} className="border-b border-[#35271e] last:border-b-0 hover:bg-[#251b15]/60 transition-colors">
                      <td className="p-3 md:p-4 font-mono text-sm text-zinc-200">{inv.number}</td>
                      <td className="p-3 md:p-4 text-sm text-zinc-200">{inv.client_name}</td>
                      <td className="p-3 md:p-4 text-right font-mono text-sm text-zinc-100">{fmtMoney(inv.total, inv.currency)}</td>
                      <td className="p-3 md:p-4">
                        <span className={`text-[10px] uppercase tracking-[0.18em] ${
                          inv.status === "paid" ? "text-emerald-400" :
                          inv.status === "overdue" ? "text-rose-400" :
                          inv.status === "sent" ? "text-amber-300" : "text-zinc-500"
                        }`}>
                          {inv.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
